/* ============================================================================
   BB pager — the dots + play control that sits under a horizontal product row.

   One implementation, two very different carousels:

     rail      the arrivals rail, a native overflow-x scroller. Position is
               scrollLeft; moving is a hand-rolled tween.
     flickity  recently viewed, a transform-based Flickity carousel. Position
               is selectedIndex; moving is flickity.select().

   Everything above the driver line is shared: dot building, the active dot,
   autoplay, the pause rules and reduced motion. Adding a third carousel means
   writing a driver, not another pager.

   STEPS ONE ITEM AT A TIME (Eric, 2026-10-01: "add more dots so we do one by
   one"). It used to page by a whole viewport. One dot per reachable position.

   AUTOPLAY STARTS ON LANDING (Eric, 2026-10-01: "it should be in play mode on
   landing"). That is a deliberate exception to the Brand-OS motion rule "no
   autoplaying carousels that move without user input on product surfaces" —
   Eric asked for it directly after the rule was raised twice. Because it now
   DOES move unprompted, WCAG 2.2.2 applies, which is why the pause control is
   not optional. Reduced-motion visitors never get autoplay at all; that one is
   a hard requirement and is not Eric's to waive.
   ========================================================================= */
(function () {
  'use strict';

  // The recently-viewed snippet renders in three places (the standalone
  // section and twice in related.liquid), so this file can be requested more
  // than once per page. Run the setup exactly once.
  if (window.__bbPagerLoaded) return;
  window.__bbPagerLoaded = true;

  var AUTOPLAY_MS = 4500;
  var SCROLL_MS = 700;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(reduce && reduce.matches); }

  /* Solves a real cubic-bezier instead of approximating it, so the curve IS
     the Brand-OS token (ease-in-out, .4/0/.2/1) and not a lookalike. */
  function bezier(x1, y1, x2, y2) {
    function A(a, b) { return 1 - 3 * b + 3 * a; }
    function B(a, b) { return 3 * b - 6 * a; }
    function C(a) { return 3 * a; }
    function calc(t, a, b) { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
    function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
    return function (x) {
      var t = x;
      for (var i = 0; i < 5; i++) {
        var s = slope(t, x1, x2);
        if (s === 0) break;
        t -= (calc(t, x1, x2) - x) / s;
      }
      return calc(t, y1, y2);
    };
  }
  var ease = bezier(0.4, 0, 0.2, 1);

  /* ---- driver: any native overflow-x scroller ----------------------------
     Used directly by the arrivals rail, and by recently viewed on phones,
     where Flickity disables ITSELF (flickity-disabled-mobile + watchCSS) and
     the row falls back to a plain CSS scroller. */
  function scrollDriver(root, getTrack, itemSel) {
    var raf = null;

    function track() { return getTrack(); }

    // Selectors are tried in order, not combined: .bb-tile lives INSIDE
    // .bb-tile__wrap, so one combined selector counts every product twice.
    function tiles(t) {
      if (!t) return [];
      for (var i = 0; i < itemSel.length; i++) {
        var found = t.querySelectorAll(itemSel[i]);
        if (found.length) return found;
      }
      return [];
    }

    function step(t) {
      var tile = tiles(t)[0];
      if (!tile) return 0;
      var gap = parseFloat(getComputedStyle(t).columnGap || getComputedStyle(t).gap) || 0;
      return tile.getBoundingClientRect().width + gap;
    }

    return {
      el: track,
      count: function () {
        var t = track();
        if (!t || !t.clientWidth) return 0;
        var n = tiles(t).length;
        var s = step(t);
        if (!n || !s) return 0;
        // Derive the count from how far the rail can ACTUALLY scroll, not from
        // how many whole tiles fit. `items - visible + 1` looked right and was
        // not: with 8 tiles of 318px in a 1241px viewport it gave 6 dots while
        // the rail only travels 1333px, or 4.19 steps. The 6th dot wanted
        // 1590px, clamped to 1333 and read back as dot 5, so autoplay reached
        // the end and stuck there instead of wrapping.
        // ceil + 1 keeps a final dot for the leftover, and go() sends that one
        // to maxScroll so the last item is fully shown.
        var max = t.scrollWidth - t.clientWidth;
        if (max <= 1) return 1;
        return Math.max(1, Math.min(n, Math.ceil(max / s) + 1));
      },
      index: function () {
        var t = track();
        var s = step(t);
        if (!t || !s) return 0;
        var max = t.scrollWidth - t.clientWidth;
        var last = this.count() - 1;
        // Scrolled to the very end means the LAST dot, even though the leftover
        // is a fraction of a step and would otherwise round down to the one
        // before it.
        if (max > 1 && t.scrollLeft >= max - 2) return Math.max(0, last);
        return Math.max(0, Math.min(last, Math.round(t.scrollLeft / s)));
      },
      go: function (i, done) {
        var t = track();
        if (!t) return;
        var s = step(t);
        var max = t.scrollWidth - t.clientWidth;
        // The last dot goes to the true end, so the final item is fully shown
        // rather than left cut off by the leftover.
        var want = (i >= this.count() - 1) ? max : i * s;
        var to = Math.max(0, Math.min(max, want));
        this.cancel();
        t.classList.add('is-tweening');   // suspends scroll-snap + smooth, which both fight a tween
        if (reduced()) {
          t.scrollLeft = to;
          t.classList.remove('is-tweening');
          if (done) done();
          return;
        }
        var from = t.scrollLeft, delta = to - from, t0 = performance.now();
        (function frame(now) {
          var p = Math.min(1, (now - t0) / SCROLL_MS);
          t.scrollLeft = from + delta * ease(p);
          if (p < 1) { raf = requestAnimationFrame(frame); }
          else { raf = null; t.classList.remove('is-tweening'); if (done) done(); }
        })(t0);
      },
      cancel: function () {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        var t = track();
        if (t) t.classList.remove('is-tweening');
      },
      onChange: function (fn) {
        var r = null;
        root.addEventListener('scroll', function (e) {
          if (e.target !== track()) return;
          if (r) return;
          r = requestAnimationFrame(function () { r = null; fn(); });
        }, true);
        root.querySelectorAll('[role="tab"]').forEach(function (tab) {
          tab.addEventListener('click', fn);
        });
      }
    };
  }

  function railDriver(root) {
    return scrollDriver(
      root,
      function () { return root.querySelector('.bb-rail__track:not([hidden])'); },
      ['.bb-tile__wrap', '.bb-tile']
    );
  }

  /* ---- driver: Flickity -------------------------------------------------- */
  function flickityDriver(root) {
    var host = root.querySelector('flickity-carousel') ||
               (root.closest ? root.closest('flickity-carousel') : null);

    function f() { return host && host.carouselInstance ? host.carouselInstance : null; }

    // Flickity is constructed AFTER this script runs, so binding 'change' once
    // at init silently did nothing and the dots never followed the carousel.
    // Hold the callback and attach it the first time the instance exists.
    var bound = false, pending = null;
    function bind() {
      if (bound || !pending) return;
      var i = f();
      if (i && i.on) { i.on('change', pending); bound = true; }
    }

    /* slides.length is NOT the number of reachable positions. With
       contain:true, every slide that fits on screen at the end shares the same
       scroll target — four products that all fit give four slides whose target
       is 0. Using slides.length drew four dots for a carousel that cannot
       move. Count DISTINCT targets instead, and return the first slide index
       at each one, so a position with no travel collapses to a single entry
       and the pager hides itself (count < 2). */
    function positions() {
      var i = f();
      if (!i || !i.slides) return [];
      var seen = [], out = [];
      i.slides.forEach(function (s, idx) {
        var t = Math.round(s.target);
        if (seen.indexOf(t) === -1) { seen.push(t); out.push(idx); }
      });
      return out;
    }

    /* On phones Flickity turns ITSELF off (flickity-disabled-mobile +
       watchCSS) and the row becomes a plain CSS scroller — which is why the
       theme shows a scrollbar line there. So this driver is a hybrid: Flickity
       when it is active, the native scroller when it is not. Without this the
       pager simply vanished on mobile, which is the one width it matters most. */
    var grid = root.querySelector('[data-recently-viewed-products], .flickity-grid');
    var fallback = scrollDriver(root, function () { return grid; }, ['.product-grid-slide', '.product-grid__item']);
    function live() { var i = f(); return !!(i && i.isActive); }

    return {
      el: function () { return host; },
      ready: function () {
        if (live()) { var i = f(); return !!(i.slides && i.slides.length); }
        return !!(grid && grid.clientWidth);     // native-scroll mode
      },
      count: function () { return live() ? positions().length : fallback.count(); },
      index: function () {
        if (!live()) return fallback.index();
        var i = f();
        if (!i) return 0;
        var p = positions();
        for (var k = p.length - 1; k >= 0; k--) { if (i.selectedIndex >= p[k]) return k; }
        return 0;
      },
      go: function (n, done) {
        if (!live()) return fallback.go(n, done);
        var i = f(), p = positions();
        if (i && p[n] != null) i.select(p[n], false, reduced());
      },
      // no `next`: the shared advance() steps through POSITIONS, so it never
      // lands on a slide that shares its target with the one before it.
      cancel: function () { if (!live()) fallback.cancel(); },
      onChange: function (fn) { pending = fn; bind(); fallback.onChange(fn); },
      bind: bind
    };
  }

  /* ---- the pager itself -------------------------------------------------- */
  function init(ctrl) {
    if (!ctrl || ctrl.dataset.bbPagerReady === '1') return;

    var mode = ctrl.dataset.bbPager || 'rail';
    var root = ctrl.closest('[data-bb-pager-root]') ||
               ctrl.closest('[data-bb-section="rail"]') ||
               ctrl.parentElement;
    var drv = mode === 'flickity' ? flickityDriver(root) : railDriver(root);

    var dotsEl = ctrl.querySelector('[data-bb-rail-dots]');
    var playBtn = ctrl.querySelector('[data-bb-rail-play]');
    if (!dotsEl || !playBtn) return;

    var timer = null, count = 0, active = 0, resizeT = null, started = false;

    function build() {
      if (drv.bind) drv.bind();   // the carousel may only now exist
      if (drv.ready && !drv.ready()) { ctrl.hidden = true; return; }
      count = drv.count();
      if (count < 2) { stop(); ctrl.hidden = true; return; }
      ctrl.hidden = false;
      if (dotsEl.childElementCount !== count) {
        dotsEl.textContent = '';
        for (var i = 0; i < count; i++) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'bb-rail__dot';
          b.setAttribute('aria-label', 'Go to item ' + (i + 1) + ' of ' + count);
          b.dataset.i = i;
          dotsEl.appendChild(b);
        }
      }
      sync();
      autostart();
    }

    function sync() {
      if (!count) return;
      active = Math.max(0, Math.min(count - 1, drv.index()));
      for (var i = 0; i < dotsEl.children.length; i++) {
        if (i === active) dotsEl.children[i].setAttribute('aria-current', 'true');
        else dotsEl.children[i].removeAttribute('aria-current');
      }
    }

    function advance() {
      if (drv.next) { drv.next(); return; }
      drv.go((active + 1) % count);
    }

    function start() {
      if (reduced() || count < 2) return;
      stop();
      timer = setInterval(advance, AUTOPLAY_MS);
      playBtn.setAttribute('aria-pressed', 'true');
      playBtn.setAttribute('aria-label', 'Pause');
      ctrl.classList.add('is-playing');
    }

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
      playBtn.setAttribute('aria-pressed', 'false');
      playBtn.setAttribute('aria-label', 'Play');
      ctrl.classList.remove('is-playing');
    }

    // Plays on landing, but only once, only when it is actually on screen, and
    // never for reduced motion. Starting while off-screen would burn through
    // the row before anybody saw it.
    function autostart() {
      if (started || reduced() || count < 2) return;
      started = true;
      if (!('IntersectionObserver' in window)) { start(); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { start(); io.unobserve(en.target); }
        });
      }, { threshold: 0.25 });
      io.observe(ctrl);
    }

    playBtn.addEventListener('click', function () { timer ? stop() : start(); });

    dotsEl.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.bb-rail__dot');
      if (!b) return;
      stop();                                   // a deliberate jump takes over
      drv.go(parseInt(b.dataset.i, 10), sync);
    });

    drv.onChange(sync);

    // Hands-on interaction with the row cancels autoplay and hands back control.
    root.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('[data-bb-rail-ctrl]')) return;
      drv.cancel();
      stop();
    }, true);

    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (en) { if (!en.isIntersecting) stop(); });
      }, { threshold: 0 }).observe(ctrl);
    }
    if (reduce && reduce.addEventListener) {
      reduce.addEventListener('change', function () { if (reduced()) stop(); });
    }

    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(build, 150);
    });

    build();

    // Recently-viewed products are fetched after load and Flickity adds its
    // cells as they arrive, so the count keeps growing for a second or two.
    // Settling on the FIRST answer is how the pager ended up showing 4 dots
    // for 6 slides, so keep rebuilding until the count stops changing rather
    // than stopping as soon as something rendered.
    var tries = 0, lastCount = count;
    var poll = setInterval(function () {
      tries++;
      if (drv.bind) drv.bind();            // catch the instance the moment it appears
      var now = drv.count();
      if (now !== lastCount) { lastCount = now; build(); }
      else sync();                          // keep the dot honest until 'change' is bound
      if (tries > 24) clearInterval(poll);
    }, 250);
    ctrl.dataset.bbPagerReady = '1';
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-bb-rail-ctrl]').forEach(init);
  }

  // Product recommendations are fetched from Shopify AFTER load and the theme
  // swaps the markup in, so that pager arrives as fresh DOM long after every
  // init event has fired. It sat there uninitialised and invisible. Watch for
  // controls that appear late rather than assuming they all exist up front.
  var watching = false;
  function watch() {
    if (watching || !('MutationObserver' in window) || !document.body) return;
    watching = true;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (!n || n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-bb-rail-ctrl]')) init(n);
          else if (n.querySelector && n.querySelector('[data-bb-rail-ctrl]')) initAll(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') { initAll(); watch(); }
  document.addEventListener('DOMContentLoaded', function () { initAll(); watch(); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('turbo:load', function () { initAll(); });
  document.addEventListener('page:loaded', function () { initAll(); });

  window.bbPagerInit = initAll;
})();
